// Copyright (c) [2025] [Your Name]
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import {
  ChatContainer,
  ChatCard,
  UserList,
  UserListHeader,
  UserItem,
  ChatMessages,
  MessageBubble,
  EmoticonContainer,
  EmoticonButton,
  InputField,
  Button,
  Modal,
  ColorPickerButton,
} from '../styles/ChatStyles';
import buttonImage from '../buttonimage.webp'; // Add this import
// Botpress webchat will be embedded via script
import { v4 as uuidv4 } from 'uuid';

const Chat = ({ goBack, goHome }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [canAccessChat, setCanAccessChat] = useState(undefined); // undefined = loading, false = not eligible, true = eligible
  const [nickname, setNickname] = useState('');
  const nicknameRef = useRef('');
  const [users, setUsers] = useState([{ nickname: 'Use CompanionBot', isBot: true, statusWords: 'click lower right corner' }]);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [fontColor, setFontColor] = useState('#000000');
  const [bubbleColor, setBubbleColor] = useState('#3B82F6');
  const statusWordsRef = useRef('');
  const normalize = (s) => (s || '').toString().trim().toLowerCase();
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showChatRulesModal, setShowChatRulesModal] = useState(false);
  const [statusWordsInput, setStatusWordsInput] = useState('');
  const [statusWordsError, setStatusWordsError] = useState('');
  const [showStatusWords, setShowStatusWords] = useState(true);
  const chatMessagesRef = useRef(null);
  const presenceChannelRef = useRef(null);
  const messageChannelRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const processedBroadcastIds = useRef(new Set());
  // removed unused refs: isInitialized, hasUserInteracted, lastUserMessage
  const botConversation = useRef([]);
  const lastSentMessage = useRef('');

  // Embed Botpress webchat widget on mount (latest Botpress recommended method)
  useEffect(() => {
    // Only inject once
    if (document.getElementById('bp-webchat-v3')) return;
    // Inject v3.6 inject.js
    const injectScript = document.createElement('script');
    injectScript.id = 'bp-webchat-v3';
    injectScript.src = 'https://cdn.botpress.cloud/webchat/v3.6/inject.js';
    injectScript.async = true;
    document.body.appendChild(injectScript);

    // Inject config .js (defer)
    const configScript = document.createElement('script');
    configScript.id = 'bp-webchat-config';
    configScript.src = 'https://files.bpcontent.cloud/2025/08/07/02/20250807023510-7V7T00YU.js';
    configScript.defer = true;
    document.body.appendChild(configScript);

    return () => {
      // Remove widget and scripts on unmount
      const frame = document.getElementById('bp-web-widget-container');
      if (frame) frame.remove();
      injectScript.remove();
      configScript.remove();
    };
  }, []);


  // Only clear messages and refs on mount, not on every render
  useEffect(() => {
    setMessages([]);
    processedMessageIds.current.clear();
    processedBroadcastIds.current.clear();
    botConversation.current = [];
    lastSentMessage.current = '';
  }, []);

  // Guard to ensure we only track presence once per session
  const hasTrackedPresence = useRef(false);

  useEffect(() => {
    let isMounted = true;
    const checkMembership = async () => {
      try {
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) {
          console.error('User fetch error:', userError.message);
          if (isMounted) setCanAccessChat(false);
          return;
        }
        if (user) {
          setCurrentUserId(user.id);
          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('nickname, first_name, membership_type, font_color, bubble_color, status_words')
            .eq('id', user.id)
            .single();
          if (error) {
            console.error('Membership check error:', error.message);
            if (isMounted) setCanAccessChat(false);
            return;
          }
          if (profile) {
            const newNickname = profile.nickname || profile.first_name || 'User';
            setNickname(newNickname);
            nicknameRef.current = newNickname;
            setFontColor(profile.font_color || '#000000');
            setBubbleColor(profile.bubble_color || '#3B82F6');
            const newStatusWords = profile.status_words || '';
            statusWordsRef.current = newStatusWords;
            const isEligible = ['Gift Subscription', 'Monthly Subscription', 'Yearly Subscription', 'Admin'].includes(profile.membership_type);
            if (isMounted) setCanAccessChat(isEligible);
          } else {
            if (isMounted) setCanAccessChat(false);
          }
        } else {
          if (isMounted) setCanAccessChat(false);
        }
      } catch (err) {
        console.error('Unexpected membership error:', err);
        if (isMounted) setCanAccessChat(false);
      }
    };
    checkMembership();
    return () => { isMounted = false; };
  }, []);

  const handlePresenceSync = useCallback(() => {
    if (presenceChannelRef.current) {
      const state = presenceChannelRef.current.presenceState();
      const presentUsers = Object.values(state).flat().map(user => ({
        nickname: user.nickname || 'User',
        isBot: user.isBot || false,
        statusWords: user.statusWords || ''
      }));
      const uniqueUsers = presentUsers.reduce((acc, user) => {
        if (!acc.some(u => u.nickname === user.nickname)) {
          acc.push(user);
        }
        return acc;
      }, []);
      // Merge presence users and ensure current user (if known) is present
      let merged = uniqueUsers.filter(user => !user.isBot);
      if (nicknameRef.current) {
        const normNick = normalize(nicknameRef.current);
        const foundIdx = merged.findIndex(u => normalize(u.nickname) === normNick);
        if (foundIdx === -1) {
          // If current user not present in presence snapshot, add them with saved statusWords
          merged.unshift({ nickname: nicknameRef.current, isBot: false, statusWords: statusWordsRef.current || '' });
        } else {
          // Ensure the current user's statusWords prefer the locally saved value
          merged[foundIdx] = { ...merged[foundIdx], statusWords: statusWordsRef.current || merged[foundIdx].statusWords };
        }
      }
        console.log('handlePresenceSync merged users:', merged.map(u => ({ nickname: u.nickname, statusWords: u.statusWords })));
        setUsers([{ nickname: 'Use CompanionBot', isBot: true, statusWords: 'click lower right corner' }, ...merged]);
    }
  }, []);

  // Botpress webchat is now embedded as a widget, not as a React hook

  // Set up channels only after eligibility is confirmed
  useEffect(() => {
    if (canAccessChat !== true) {
      setShowChatRulesModal(false);
      return;
    }

    const hasAcceptedChatRules = localStorage.getItem('companionJulieChatRulesAccepted') === 'true';
    setShowChatRulesModal(!hasAcceptedChatRules);

    // subscription refs created; cleanup will remove channels on unmount
    presenceChannelRef.current = supabase.channel('chat_presence', { config: { presence: { key: 'users' } } });

    presenceChannelRef.current
      .on('presence', { event: 'sync' }, handlePresenceSync)
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        newPresences.forEach(user => {
          if (user.nickname && normalize(user.nickname) !== normalize(nicknameRef.current)) {
            // Only announce if not already in messages
            setMessages(prev => {
              if (prev.some(m => m.is_system && m.text === `${user.nickname} joined the chat.`)) return prev;
              const systemMsg = {
                text: `${user.nickname} joined the chat.`,
                is_system: true,
                messageId: uuidv4(),
              };
              if (messageChannelRef.current) {
                console.log('Broadcasting join message:', systemMsg);
                messageChannelRef.current.send({
                  type: 'broadcast',
                  event: 'new_message',
                  payload: systemMsg,
                });
              }
              return [...prev, systemMsg];
            });
          }
        });
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        leftPresences.forEach(user => {
          if (user.nickname && normalize(user.nickname) !== normalize(nicknameRef.current)) {
            setMessages(prev => {
              if (prev.some(m => m.is_system && m.text === `${user.nickname} left the chat.`)) return prev;
              const systemMsg = {
                text: `${user.nickname} left the chat.`,
                is_system: true,
                messageId: uuidv4(),
              };
              if (messageChannelRef.current) {
                console.log('Broadcasting leave message:', systemMsg);
                messageChannelRef.current.send({
                  type: 'broadcast',
                  event: 'new_message',
                  payload: systemMsg,
                });
              }
              return [...prev, systemMsg];
            });
          }
        });
      })
      .subscribe();

    messageChannelRef.current = supabase.channel('chat_messages');
    messageChannelRef.current
      .on('broadcast', { event: 'new_message' }, ({ payload }) => {
        if (!processedBroadcastIds.current.has(payload.messageId)) {
          processedBroadcastIds.current.add(payload.messageId);
          setMessages(prev => {
            // Only add if not already present in prev
            if (payload.messageId && prev.some(m => m.messageId === payload.messageId)) {
              return prev;
            }
            console.log('Received broadcast message:', payload);
            return [...prev, payload];
          });
        }
      })
      .subscribe();

    return () => {
      if (presenceChannelRef.current) supabase.removeChannel(presenceChannelRef.current);
      if (messageChannelRef.current) supabase.removeChannel(messageChannelRef.current);
    };
  }, [canAccessChat, handlePresenceSync]);

  // Track presence only after eligibility and nickname are set
  useEffect(() => {
    if (canAccessChat !== true || !nicknameRef.current || !presenceChannelRef.current || hasTrackedPresence.current) return;
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user && nicknameRef.current) {
        await presenceChannelRef.current.track({ nickname: nicknameRef.current, isBot: false, statusWords: statusWordsRef.current });
        hasTrackedPresence.current = true;
      }
    })();
  }, [canAccessChat, nickname]);

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  const handleEmoticonClick = (emoticon) => {
    setCurrentMessage(prev => prev + emoticon);
  };

  const handleChatRulesAcknowledgement = () => {
    localStorage.setItem('companionJulieChatRulesAccepted', 'true');
    setShowChatRulesModal(false);
  };

  const handleUserClick = (userNickname) => {
    console.log('handleUserClick called with', { userNickname, nicknameState: nickname, nicknameRef: nicknameRef.current });
    // If the user clicked their own name, open the profile/menu only
    if (normalize(userNickname) === normalize(nicknameRef.current) && !userNickname.includes('@CompanionBot')) {
      console.log('Opening profile for own user', nicknameRef.current);
      setShowColorPicker(true);
      return;
    }
    // For other users, append their name to the message input
    setCurrentMessage(prev => {
      const trimmed = prev.trim();
      const next = trimmed ? `${trimmed} ${userNickname}` : userNickname;
      console.log('Setting currentMessage to', next);
      return next;
    });
  };

  const handleColorChange = (newFontColor, newBubbleColor) => {
    if (newFontColor) setFontColor(newFontColor);
    if (newBubbleColor) setBubbleColor(newBubbleColor);
  };

  const handleStatusWordsChange = (e) => {
    const input = e.target.value;
    if (input.length > 50) {
      setStatusWordsError('Status words cannot exceed 50 characters.');
    } else {
      setStatusWordsError('');
      setStatusWordsInput(input);
    }
  };

  const saveProfileChanges = async () => {
    if (statusWordsError) {
      alert('Please fix the errors before saving.');
      return;
    }
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        console.error('Error fetching user:', userError);
        alert('User not logged in!');
        return;
      }
      const { error } = await supabase
        .from('user_profiles')
        .update({
          font_color: fontColor,
          bubble_color: bubbleColor,
          status_words: statusWordsInput,
        })
        .eq('id', user.id);
      if (error) {
        console.error('Error updating profile:', error.message);
        alert('Failed to save profile changes!');
        return;
      }
      statusWordsRef.current = statusWordsInput;
      setShowColorPicker(false);
      if (presenceChannelRef.current) {
        await presenceChannelRef.current.track({ nickname: nicknameRef.current, isBot: false, statusWords: statusWordsInput });
      }
      // Update local users list so UI reflects the new status words immediately.
      // If the current user is not yet in the users list (presence not tracked), add them.
      setUsers(prev => {
        const exists = prev.some(u => normalize(u.nickname) === normalize(nicknameRef.current));
        if (exists) {
          console.log('Updating existing local user statusWords for', nicknameRef.current, statusWordsInput);
          return prev.map(u => normalize(u.nickname) === normalize(nicknameRef.current) ? { ...u, statusWords: statusWordsInput } : u);
        }
        console.log('Adding local user to users list with statusWords', nicknameRef.current, statusWordsInput);
        return [{ nickname: nicknameRef.current, isBot: false, statusWords: statusWordsInput }, ...prev];
      });
    } catch (err) {
      console.error('Unexpected profile update error:', err);
      alert('Unexpected error saving profile changes!');
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim()) return;
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('User fetch error:', userError.message);
        alert('User not logged in!');
        return;
      }
      if (!user) {
        alert('User not logged in!');
        return;
      }
      const cleanedMessage = currentMessage.trim();
      const isAddressed = cleanedMessage.toLowerCase().includes('@companionbot');
      const newMessage = {
        user_id: user.id,
        nickname,
        text: cleanedMessage,
        is_bot: false,
        is_system: false,
        messageId: uuidv4(),
      };
      setMessages(prev => [...prev, newMessage]);
      processedBroadcastIds.current.add(newMessage.messageId);
      if (messageChannelRef.current) {
        console.log('Broadcasting user message:', newMessage);
        messageChannelRef.current.send({
          type: 'broadcast',
          event: 'new_message',
          payload: newMessage,
        });
      }
      setCurrentMessage('');
      // Botpress webchat widget is now embedded, so direct client calls are not available
      // If you want to send a message to the bot, use the widget interface
      if (isAddressed) {
        // Optionally, you can display a message or guide the user to use the widget
        alert('To chat with CompanionBot, please use the Botpress chat widget in the lower right corner.');
      } else {
        console.log('Unaddressed message:', cleanedMessage);
      }
    } catch (err) {
      console.error('Unexpected send message error:', err.message);
      alert(`Failed to send message: ${err.message}`);
    }
  };

  const [isUserListVisible, setIsUserListVisible] = useState(false);
  const isMobile = window.innerWidth <= 600;

  return (
    <ChatContainer
      style={{
        maxWidth: isMobile ? '100vw' : '900px',
        margin: isMobile ? '0' : '0 auto',
        padding: isMobile ? '0 0px' : '0 40px',
        height: '100vh',
        overflow: 'hidden',
        position: 'relative',
        background: isMobile ? '#F9FAFB' : undefined,
      }}
      role="main"
      aria-label="Chat main content"
    >
      {/* Visually hidden screen reader instructions */}
      <div style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-live="polite">
        Welcome to the chat room. Use Tab to navigate between the user list, chat messages, and message input. To send a message, type in the input and press Enter. To access CompanionBot, click the widget in the lower right corner.
      </div>
      {showChatRulesModal && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(17, 24, 39, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            maxWidth: '600px',
            width: '100%',
            padding: '24px 20px',
            boxShadow: '0 20px 45px rgba(0,0,0,0.25)',
            border: '2px solid #111827',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: '0 0 16px',
              fontFamily: 'Times New Roman, Times, serif',
              fontSize: '26px',
              color: '#1F2937',
            }}>
              Chat Room Notice
            </h3>
            <p style={{
              fontFamily: 'Arial, sans-serif',
              fontSize: '16px',
              lineHeight: 1.6,
              color: '#374151',
              margin: '0 0 20px',
              textAlign: 'left',
            }}>
              The App Does not permit the public sharing of real-world, graphic violence outside of a newsworthy context. Any issues can be reported on the My Account page. Screenshots will be asked for as proof of ban worthy offense. Offensive Users will be banned from Chat room for life. Thank you.
            </p>
            <Button
              onClick={handleChatRulesAcknowledgement}
              style={{
                backgroundImage: `url(${buttonImage})`,
                backgroundSize: 'cover',
                color: 'white',
                border: '2px solid #111827',
                borderRadius: '10px',
                fontFamily: 'Times New Roman, Times, serif',
                fontWeight: 'bold',
                fontSize: '18px',
                padding: '10px 24px',
                cursor: 'pointer',
              }}
            >
              OK
            </Button>
          </div>
        </div>
      )}

      {canAccessChat === undefined ? (
        <div style={{ textAlign: 'center', marginTop: 40 }}>Loading chat...</div>
      ) : canAccessChat ? (
        <>
          <div style={{
            width: '100%',
            textAlign: 'center',
            margin: isMobile ? '10px 0 4px 0' : '18px 0 8px 0',
            fontFamily: 'Times New Roman, Times, serif',
            fontWeight: 'bold',
            fontSize: isMobile ? '18px' : '22px',
            letterSpacing: '1px',
            color: '#1F2937'
          }}
            role="heading"
            aria-level={1}
          >
            Chat Room
          </div>
          <ChatCard
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              maxWidth: '100%',
              width: '100%',
              margin: isMobile ? '0' : '0 auto',
              maxHeight: isMobile ? 'calc(100vh - 60px)' : 'calc(100vh - 120px)',
              overflow: 'hidden',
              boxSizing: 'border-box',
              background: isMobile ? '#F9FAFB' : undefined,
            }}
          >
            {(isMobile ? isUserListVisible : true) && (
              <div
                style={{
                  minWidth: isMobile ? '100vw' : 160,
                  maxWidth: isMobile ? '100vw' : 200,
                  marginRight: isMobile ? 0 : 10,
                  height: isMobile ? 'auto' : 'calc(100vh - 120px)',
                  overflowY: 'auto',
                  background: '#fff',
                  borderRight: isMobile ? 'none' : '1px solid #E5E7EB',
                  borderTop: isMobile ? '1px solid #E5E7EB' : 'none',
                  boxSizing: 'border-box',
                  position: 'relative',
                  left: 0,
                  zIndex: 2,
                  display: 'block',
                  borderBottom: isMobile ? '1px solid #E5E7EB' : undefined,
                  maxHeight: isMobile ? 150 : undefined,
                }}
                role="navigation"
                aria-label="Chat participants list"
                tabIndex={0}
              >
                <UserList
                  $isOpen={true}
                  style={{
                    width: '100%',
                    minWidth: isMobile ? '100vw' : 160,
                    maxWidth: isMobile ? '100vw' : 200,
                    marginLeft: 0,
                    marginRight: 0,
                    maxHeight: isMobile ? 150 : '100%',
                    overflowY: 'auto',
                    borderRight: 'none',
                    borderTop: 'none',
                    background: 'transparent'
                  }}
                  role="list"
                  aria-label="Participants"
                >
                  <UserListHeader>
                    <input
                      type="checkbox"
                      checked={showStatusWords}
                      onChange={(e) => setShowStatusWords(e.target.checked)}
                      aria-label="Show participant status messages"
                    /> Show Status
                  </UserListHeader>
                  {users.map((user, index) => (
                    <UserItem
                      key={index}
                      $isBot={user.isBot}
                      $isCurrentUser={normalize(user.nickname) === normalize(nickname)}
                      onClick={() => handleUserClick(user.nickname)}
                      role="listitem"
                      tabIndex={0}
                      aria-label={user.isBot ? `${user.nickname}, bot, ${user.statusWords}` : `${user.nickname}${(normalize(user.nickname) === normalize(nickname) && statusWordsRef.current) ? ', ' + statusWordsRef.current : (user.statusWords ? ', ' + user.statusWords : '')}`}
                    >
                      {user.nickname}
                      {showStatusWords && ((normalize(user.nickname) === normalize(nickname) && statusWordsRef.current) || user.statusWords) && (
                        <div
                          style={{
                            fontStyle: 'italic',
                            fontSize: '12px',
                            color: user.isBot ? '#10B981' : '#6B7280',
                            margin: '0'
                          }}
                        >
                          {normalize(user.nickname) === normalize(nickname) ? statusWordsRef.current : user.statusWords}
                        </div>
                      )}
                    </UserItem>
                  ))}
                </UserList>
              </div>
            )}
            {isMobile && (
              <Button
                onClick={() => setIsUserListVisible(v => !v)}
                style={{
                  margin: '6px 0 0 0',
                  width: '100vw',
                  borderRadius: 0,
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  borderBottom: '1px solid #E5E7EB',
                  background: '#F3F4F6',
                  color: '#1F2937',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  boxShadow: 'none',
                  position: 'relative',
                  zIndex: 3,
                }}
              >
                {isUserListVisible ? 'Hide Participants' : 'Show Participants'}
              </Button>
            )}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                maxWidth: isMobile ? '100vw' : 'calc(100% - 200px)',
                maxHeight: isMobile ? 'calc(100vh - 260px)' : 'calc(100vh - 120px)',
                minWidth: 0,
                background: isMobile ? '#F9FAFB' : undefined,
              }}
            >
              <ChatMessages
                ref={chatMessagesRef}
                style={{
                  flexGrow: 1,
                  overflowY: 'auto',
                  borderRadius: isMobile ? 0 : '15px',
                  height: isMobile ? 'calc(100vh - 260px)' : 'calc(100vh - 120px)',
                  paddingRight: 0,
                  paddingLeft: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0',
                  scrollBehavior: 'smooth',
                  background: isMobile ? '#F9FAFB' : undefined,
                }}
                role="log"
                aria-live="polite"
                aria-label="Chat messages"
                tabIndex={0}
              >
                {[...messages.reduce((map, msg) => {
                  if (!msg.messageId) return map;
                  if (!map.has(msg.messageId)) map.set(msg.messageId, msg);
                  return map;
                }, new Map()).values()].map((msg, index) => (
                  <div
                    key={msg.messageId || index}
                    style={{
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: msg.user_id === currentUserId ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      margin: '0',
                    }}
                  >
                    <MessageBubble
                      $isSystem={msg.is_system}
                      $isBot={msg.is_bot}
                      $isCurrentUser={msg.user_id === currentUserId}
                      $bubbleColor={msg.user_id === currentUserId ? bubbleColor : msg.is_bot ? '#D1D5DB' : '#E5E7EB'}
                      $fontColor={msg.user_id === currentUserId ? fontColor : msg.is_bot ? '#000000' : '#374151'}
                      style={{
                        margin: '4px 0',
                        padding: isMobile ? '8px 10px' : '6px 12px',
                        lineHeight: '1.4',
                        display: 'inline-block',
                        maxWidth: isMobile ? '95vw' : '80%',
                        minWidth: '40px',
                        wordBreak: 'break-word',
                        width: 'fit-content',
                        whiteSpace: 'pre-line',
                        fontSize: isMobile ? '15px' : undefined,
                        borderRadius: isMobile ? '10px' : undefined,
                      }}
                    >
                      <strong>{msg.nickname}:</strong> {msg.text}
                    </MessageBubble>
                  </div>
                ))}
              </ChatMessages>
              <div style={{
                padding: isMobile ? '6px' : '10px',
                borderTop: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
                width: '100%',
                marginLeft: '0',
                marginBottom: isMobile ? '4px' : '10px'
              }}>
                <EmoticonContainer style={{ marginBottom: isMobile ? '2px' : '5px', gap: 0 }}>
                  {['😊', '👍', '🚀', '😂', '❤️', '😎', '🎉', '😍', '🤔', '😄', '🙌', '🎈', '😢', '😀', '🤓', '😇', '🥳', '😜', '🤗', '😱'].map((emoticon, index) => (
                    <EmoticonButton key={index} onClick={() => handleEmoticonClick(emoticon)} style={{ margin: 0, padding: isMobile ? '0 1px' : '0 2px', fontSize: isMobile ? '17px' : '18px' }}>
                      {emoticon}
                    </EmoticonButton>
                  ))}
                </EmoticonContainer>
                <InputField
                  type="text"
                  value={currentMessage}
                  onChange={(e) => setCurrentMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  placeholder="Type your message..."
                  autoFocus
                  aria-label="Type your message and press Enter to send"
                  style={{
                    fontSize: isMobile ? '15px' : undefined,
                    padding: isMobile ? '7px' : undefined,
                  }}
                />
              </div>
            </div>
          </ChatCard>
          {showColorPicker && (
            <Modal>
              <h3 style={{ fontFamily: 'Arial, sans-serif', fontSize: '18px', fontWeight: 'bold', color: '#1F2937', marginBottom: '10px' }}>
                Customize Profile
              </h3>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#4B5563' }}>Font Color:</label>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginTop: '5px',
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                  justifyContent: 'flex-start',
                }}>
                  {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FF00FF'].map(color => (
                    <ColorPickerButton
                      key={color}
                      color={color}
                      style={{
                        backgroundImage: 'none',
                        border: '2px solid #ccc',
                        margin: 0,
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        minHeight: 28,
                        maxWidth: 28,
                        maxHeight: 28,
                        padding: 0,
                      }}
                      onClick={() => handleColorChange(color, null)}
                    />
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#4B5563' }}>Bubble Color:</label>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginTop: '5px',
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                  justifyContent: 'flex-start',
                }}>
                  {['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6'].map(color => (
                    <ColorPickerButton
                      key={color}
                      color={color}
                      style={{
                        backgroundImage: 'none',
                        border: '2px solid #ccc',
                        margin: 0,
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        minHeight: 28,
                        maxWidth: 28,
                        maxHeight: 28,
                        padding: 0,
                      }}
                      onClick={() => handleColorChange(null, color)}
                    />
                  ))}
                </div>
              </div>
              <div style={{ marginBottom: '10px' }}>
                <label style={{ fontFamily: 'Arial, sans-serif', fontSize: '14px', color: '#4B5563' }}>Status Words:</label>
                <input
                  type="text"
                  value={statusWordsInput}
                  onChange={handleStatusWordsChange}
                  placeholder="Enter status words (max 50 chars)"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '4px',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                  }}
                />
                {statusWordsError && (
                  <p style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{statusWordsError}</p>
                )}
              </div>
              <Button onClick={saveProfileChanges}>Save & Close</Button>
            </Modal>
          )}
        </>
      ) : (
        <p style={{ fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#4B5563', textAlign: 'center' }}>
          Upgrade your membership to Gift, Monthly, Yearly Subscription, or Admin to gain Access to the Chat Room.
        </p>
      )}
      <div style={{
        marginTop: isMobile ? '20px' : '25px',
        display: 'flex',
        justifyContent: 'center',
        gap: '10px',
        width: '100%',
        position: isMobile ? 'static' : 'absolute',
        bottom: isMobile ? '10px' : '10px',
        left: 0,
        background: isMobile ? '#F9FAFB' : undefined,
        paddingBottom: isMobile ? '8px' : '0',
      }}>
        <Button
          onClick={goBack}
          style={{
            fontSize: isMobile ? '15px' : undefined,
            padding: isMobile ? '7px 14px' : undefined,
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            color: 'white',
            border: 'none', // Remove black border
            borderRadius: '5px',
            fontFamily: 'Times New Roman, Times, serif',
            fontWeight: 'bold',
            boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          Back
        </Button>
        <Button
          onClick={goHome}
          style={{
            fontSize: isMobile ? '15px' : undefined,
            padding: isMobile ? '7px 14px' : undefined,
            backgroundImage: `url(${buttonImage})`,
            backgroundSize: 'cover',
            color: 'white',
            border: 'none', // Remove black border
            borderRadius: '5px',
            fontFamily: 'Times New Roman, Times, serif',
            fontWeight: 'bold',
            boxShadow: '2px 2px 4px rgba(0,0,0,0.3)',
          }}
        >
          Home
        </Button>
      </div>
    </ChatContainer>
  );
};

export default Chat;